/**
 * Janko Piano - Renderer Module
 * Handles canvas rendering, key generation, and shape caching
 * @version 0.2.63
 */

import { CONFIG, DIAMOND_PARAMS } from './config.js';
import { activePointers } from './audio.js';

// =============================================================================
// CANVAS STATE
// =============================================================================

let canvas = null;
let ctx = null;
let bgCanvas = null;
let bgCtx = null;

export let lastCanvasCssW = 0;
let lastCanvasCssH = null;
let lastCanvasDpr = null;
let initialScrollSet = false;

// Render-on-demand state
let renderDirty = true;
let renderReq = null;
let cachedInstId = 'piano';

export let metrics = { w: 0, h: 0 };
export let keys = [];

// =============================================================================
// SHAPE CACHE
// =============================================================================

let shapeCache = {
    original: { white: null, black: null, activeRoot: null, activeOther: null, grey: null },
    circle: { white: null, black: null, activeRoot: null, activeOther: null, grey: null },
    hexagon: { white: null, black: null, activeRoot: null, activeOther: null, grey: null },
    square: { white: null, black: null, activeRoot: null, activeOther: null, grey: null },
    diamond: { white: null, black: null, activeRoot: null, activeOther: null, grey: null }
};

let lastCacheState = { w: 0, h: 0, shape: '', color: '' };

// =============================================================================
// INITIALIZATION
// =============================================================================

export function initRenderer() {
    canvas = document.getElementById('pianoCanvas');
    ctx = canvas.getContext('2d');
    
    // Background canvas for static elements (if needed)
    bgCanvas = document.createElement('canvas');
    bgCtx = bgCanvas.getContext('2d');
}

export function getCanvas() {
    return canvas;
}

export function getContext() {
    return ctx;
}

// =============================================================================
// COLOR SCHEME LOGIC
// =============================================================================

export function getKeyColorType(note) {
    const scheme = CONFIG.colorScheme;

    if (scheme === 'white') {
        return true;
    } else if (scheme === 'sixsix') {
        const whiteKeys = ['F#', 'G', 'G#', 'A', 'A#', 'B'];
        return whiteKeys.includes(note);
    } else if (scheme === 'fiveseven') {
        const whiteKeys = ['F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
        return whiteKeys.includes(note);
    } else {
        return !note.includes('#');
    }
}

// =============================================================================
// NOTE/GRID CALCULATIONS
// =============================================================================

export function getNoteFromGrid(r, c) {
    const odd = ['A#', 'C', 'D', 'E', 'F#', 'G#'];
    const even = ['A', 'B', 'C#', 'D#', 'F', 'G'];
    const seq = (r % 2 !== 0) ? odd : even;
    const note = seq[((c % 6) + 6) % 6];
    
    const off = { 'C': 0, 'C#': 1, 'D': 2, 'D#': 3, 'E': 4, 'F': 5, 'F#': 6, 'G': 7, 'G#': 8, 'A': 9, 'A#': 10, 'B': 11 };
    let oct = Math.floor(c / 6);
    let midi = (oct + 1) * 12 + off[note];
    if (off[note] < 9) midi += 12;
    
    if (CONFIG.splitOctave) {
        const p = Math.floor((CONFIG.rows - 1 - r) / 2);
        if (p > 0) midi += (12 * p);
    }
    
    return { note: note, midi: midi, isWhite: getKeyColorType(note) };
}

// =============================================================================
// KEY GENERATION
// =============================================================================

export function generateKeys(updateMinimapFn) {
    if (!canvas) initRenderer();
    
    const cont = document.getElementById('app-container');
    const mmBar = document.getElementById('minimap-bar');
    const mmH = mmBar ? mmBar.offsetHeight : 0;
    const availH = cont.clientHeight - mmH;
    const padBot = parseFloat(window.getComputedStyle(cont).paddingBottom) || 0;
    
    // High-DPI support: scale canvas for sharp rendering
    // Cap DPR for performance on low-end devices
    const rawDpr = window.devicePixelRatio || 1;
    const dpr = Math.min(rawDpr, CONFIG.maxDpr || 3);
    const cssWidth = cont.clientWidth;
    const cssHeight = availH - padBot;

    const needsResize = (
        lastCanvasCssW !== cssWidth ||
        lastCanvasCssH !== cssHeight ||
        lastCanvasDpr !== dpr
    );

    if (needsResize) {
        // Set canvas internal resolution (scaled by DPR)
        canvas.width = cssWidth * dpr;
        canvas.height = cssHeight * dpr;

        // Set CSS display size (unchanged)
        canvas.style.width = cssWidth + 'px';
        canvas.style.height = cssHeight + 'px';

        // Scale context so drawing commands use CSS pixels
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

        lastCanvasCssW = cssWidth;
        lastCanvasCssH = cssHeight;
        lastCanvasDpr = dpr;
    }

    // Use CSS dimensions for calculations (not scaled canvas dimensions)
    const canvasW = cssWidth;
    const canvasH = cssHeight;

    // Scale factor based on shape
    let scaleFactor = 1.0;
    if (CONFIG.keyShape === 'hexagon') scaleFactor = 1.333;
    else if (CONFIG.keyShape === 'circle') scaleFactor = 1.155;
    else if (CONFIG.keyShape === 'diamond') scaleFactor = DIAMOND_PARAMS.SIZE_H;

    // Row range for diamond shape
    let effRows = CONFIG.rows;
    let rStart = 0;
    let rEnd = CONFIG.rows;
    let yRowShift = 0;

    if (CONFIG.keyShape === 'diamond') {
        rStart = -1;
        rEnd = CONFIG.rows + 1;
        yRowShift = DIAMOND_PARAMS.ROW_SHIFT;
    }

    let cellH = canvasH / (effRows + (scaleFactor - 1));
    const bleed = cellH * (scaleFactor - 1);
    const startY = bleed / 2;

    let cellW;
    if (CONFIG.keyShape === 'hexagon' || CONFIG.keyShape === 'circle') {
        cellW = (cellH / 0.866) * CONFIG.zoom;
    } else if (CONFIG.keyShape === 'square') {
        cellW = cellH * CONFIG.zoom;
    } else if (CONFIG.keyShape === 'diamond') {
        cellW = (cellH * DIAMOND_PARAMS.SIZE_W) * CONFIG.zoom;
    } else {
        cellW = (cellH / 1.5) * CONFIG.zoom;
    }
    
    metrics.w = cellW;
    metrics.h = cellH;
    keys = [];
    
    const cols = canvasW / metrics.w;
    let maxOff = 42.5 - cols;
    if (maxOff < 0) maxOff = 0;
    
    // Center keyboard on first load (center around middle C / column ~21)
    if (!initialScrollSet) {
        // Target: center of keyboard (column 21) should be at center of screen
        // scrollPos * 6 = starting column, so we want startCol + cols/2 = 21
        // startCol = 21 - cols/2, scrollPos = startCol / 6
        const centerCol = 21;
        let targetStartCol = centerCol - (cols / 2);
        if (targetStartCol < 0) targetStartCol = 0;
        if (targetStartCol > maxOff) targetStartCol = maxOff;
        CONFIG.scrollPos = targetStartCol / 6;
        initialScrollSet = true;
    }
    
    const slider = document.getElementById('scroll-slider');
    if (slider) {
        slider.max = maxOff / 6;
        if (CONFIG.scrollPos > slider.max) CONFIG.scrollPos = slider.max;
    }
    
    if (updateMinimapFn) updateMinimapFn(maxOff / 6);

    const shift = -(CONFIG.scrollPos * 6 * metrics.w);

    // Extended column range for split octave
    let extraCols = CONFIG.splitOctave ? (Math.floor(CONFIG.rows / 2) * 6) : 0;
    const startCol = Math.floor(CONFIG.scrollPos * 6) - 6 - extraCols;
    const endCol = startCol + Math.ceil(cols) + 12 + (extraCols * 4);

    // Split octave bounds calculation
    let splitLeftBound = -Infinity;
    let splitRightBound = Infinity;

    if (CONFIG.splitOctave) {
        for (let testR = 0; testR < CONFIG.rows; testR++) {
            const testRowOff = (testR % 2 !== 0) ? (metrics.w / 2) : 0;
            const isEvenRow = (testR % 2 === 0);

            let minValidX = Infinity;
            let maxValidX = -Infinity;
            for (let c = -10; c < 100; c++) {
                const n = getNoteFromGrid(testR, c);
                if (n.midi >= 21 && n.midi <= 104) {
                    const x = (c * metrics.w) + shift + testRowOff;
                    if (x < minValidX) minValidX = x;
                    if (x > maxValidX) maxValidX = x;
                }
            }

            if (isEvenRow && minValidX > splitLeftBound && minValidX < Infinity) {
                splitLeftBound = minValidX;
            }
            if (maxValidX < splitRightBound && maxValidX > -Infinity) {
                splitRightBound = maxValidX;
            }
        }
    }

    // Calculate centering offset
    CONFIG.centerOffset = 0;
    if (CONFIG.splitOctave) {
        let keyboardWidth = splitRightBound - splitLeftBound + metrics.w;
        if (keyboardWidth < canvasW) {
            CONFIG.centerOffset = (canvasW - keyboardWidth) / 2 - splitLeftBound;
        }
    } else {
        let minValidX = Infinity;
        let maxValidX = -Infinity;
        for (let c = -10; c < 100; c++) {
            const n = getNoteFromGrid(0, c);
            if (n.midi >= 21 && n.midi <= 104) {
                const x = (c * metrics.w) + shift;
                if (x < minValidX) minValidX = x;
                if (x > maxValidX) maxValidX = x;
            }
        }
        let keyboardWidth = maxValidX - minValidX + metrics.w;
        if (keyboardWidth < canvasW) {
            CONFIG.centerOffset = (canvasW - keyboardWidth) / 2 - minValidX;
        }
    }

    // Generate keys
    for (let r = rStart; r < rEnd; r++) {
        const rowOff = (r % 2 !== 0) ? (metrics.w / 2) : 0;
        let splitShift = 0;

        for (let c = startCol; c < endCol; c++) {
            const x = (c * metrics.w) + shift + rowOff + splitShift + (CONFIG.centerOffset || 0);
            const n = getNoteFromGrid(r, c);
            
            if (n.midi >= 21 && n.midi <= 104) {
                if (CONFIG.splitOctave) {
                    const rawX = (c * metrics.w) + shift + rowOff;
                    if (rawX < splitLeftBound || rawX > splitRightBound) {
                        continue;
                    }
                }

                if (x > -metrics.w && x < canvasW) {
                    keys.push({
                        id: `r${r}_c${c}`,
                        x: x,
                        y: startY + ((r + yRowShift) * metrics.h),
                        w: metrics.w,
                        h: metrics.h,
                        r: r,
                        c: c,
                        note: n.note,
                        midi: n.midi,
                        isWhite: n.isWhite
                    });
                }
            }
        }
    }
    
    updateShapeCache();
    
    // Trigger render after keys are generated
    requestRender();
}

// =============================================================================
// SHAPE CACHING
// =============================================================================

function createCachedShape(w, h, color, shape, isBorder) {
    const c = document.createElement('canvas');
    c.width = w;
    c.height = h;
    const ctxC = c.getContext('2d');
    const g = CONFIG.gap;
    const dw = w - g, dh = h - g;
    const dx = g / 2, dy = g / 2;

    ctxC.fillStyle = color;
    ctxC.beginPath();

    if (shape === 'circle') {
        const scale = 0.95;
        ctxC.ellipse(w / 2, h / 2, (w / 2) * scale, (h / 2) * scale, 0, 0, 2 * Math.PI);
    } else if (shape === 'hexagon') {
        const cx = w / 2, cy = h / 2;
        const scale = 0.95;
        const rx = (w / 2) * scale, ry = (h / 2) * scale;

        const vertices = [
            { x: cx + rx, y: cy + ry * 0.5 },
            { x: cx, y: cy + ry },
            { x: cx - rx, y: cy + ry * 0.5 },
            { x: cx - rx, y: cy - ry * 0.5 },
            { x: cx, y: cy - ry },
            { x: cx + rx, y: cy - ry * 0.5 }
        ];

        const cornerRadius = Math.min(rx, ry) * 0.2;
        ctxC.beginPath();
        const lastV = vertices[5], firstV = vertices[0];
        ctxC.moveTo((lastV.x + firstV.x) / 2, (lastV.y + firstV.y) / 2);
        for (let i = 0; i < 6; i++) {
            const v = vertices[i];
            const nextV = vertices[(i + 1) % 6];
            ctxC.arcTo(v.x, v.y, nextV.x, nextV.y, cornerRadius);
        }
        ctxC.closePath();
    } else if (shape === 'square') {
        ctxC.roundRect(dx, dy, dw, dh, 6);
    } else if (shape === 'diamond') {
        const cx = dx + dw / 2, cy = dy + dh / 2;
        const pt = { x: cx, y: dy };
        const pr = { x: dx + dw, y: cy };
        const pb = { x: cx, y: dy + dh };
        const pl = { x: dx, y: cy };
        const radV = DIAMOND_PARAMS.CORNER_V;
        const radH = DIAMOND_PARAMS.CORNER_H;

        ctxC.beginPath();
        ctxC.moveTo((pl.x + pt.x) / 2, (pl.y + pt.y) / 2);
        ctxC.arcTo(pt.x, pt.y, pr.x, pr.y, radV);
        ctxC.arcTo(pr.x, pr.y, pb.x, pb.y, radH);
        ctxC.arcTo(pb.x, pb.y, pl.x, pl.y, radV);
        ctxC.arcTo(pl.x, pl.y, pt.x, pt.y, radH);
        ctxC.closePath();
    } else {
        ctxC.roundRect(dx, dy, dw, dh, 6);
    }
    ctxC.fill();

    if (isBorder) {
        ctxC.lineWidth = 1;
        ctxC.strokeStyle = 'rgba(255,255,255,0.1)';
        ctxC.stroke();
    }
    return c;
}

export function updateShapeCache() {
    const w = metrics.w, h = metrics.h;
    if (!w || !h) return;

    if (lastCacheState.w === w &&
        lastCacheState.h === h &&
        lastCacheState.shape === CONFIG.keyShape &&
        lastCacheState.color === CONFIG.colors.active) {
        return;
    }
    lastCacheState = { w, h, shape: CONFIG.keyShape, color: CONFIG.colors.active };

    const shapes = ['original', 'circle', 'hexagon', 'square', 'diamond'];
    const activeShape = CONFIG.keyShape || 'original';

    shapes.forEach(s => {
        const needed = (s === activeShape) || (s === 'original');

        if (!needed) {
            shapeCache[s] = { white: null, black: null, activeRoot: null, activeOther: null, grey: null };
            return;
        }

        let scaleX = 1.0, scaleY = 1.0;

        if (s === 'hexagon') {
            scaleX = 1.0;
            scaleY = 1.333;
        } else if (s === 'circle') {
            scaleX = 1.0;
            scaleY = 1.155;
        } else if (s === 'diamond') {
            scaleX = 1.0;
            scaleY = DIAMOND_PARAMS.SIZE_H;
        }

        const cw = w * scaleX;
        const ch = h * scaleY;

        shapeCache[s].white = createCachedShape(cw, ch, CONFIG.colors.white, s, true);
        shapeCache[s].black = createCachedShape(cw, ch, CONFIG.colors.black, s, true);
        shapeCache[s].activeRoot = createCachedShape(cw, ch, CONFIG.colors.active, s, true);
        shapeCache[s].activeOther = createCachedShape(cw, ch, invertColor(CONFIG.colors.active), s, true);
        shapeCache[s].grey = createCachedShape(cw, ch, '#444', s, true);
    });
}

// =============================================================================
// RENDERING
// =============================================================================

// Mark renderer as needing update (call this instead of render() directly)
export function requestRender(instId) {
    if (instId) cachedInstId = instId;
    renderDirty = true;
    if (!renderReq) {
        renderReq = requestAnimationFrame(doRender);
    }
}

function doRender() {
    renderReq = null;
    if (!renderDirty) return;
    renderDirty = false;
    render(cachedInstId);
}

export function render(currentInstId) {

    if (!canvas || typeof metrics === 'undefined' || typeof keys === 'undefined') {
        return;
    }
    
    // Use stored CSS dimensions for clearing (context is already scaled by DPR)
    const cssW = lastCanvasCssW;
    const cssH = lastCanvasCssH;
    ctx.clearRect(0, 0, cssW, cssH);
    
    const keyStates = new Map();
    for (const [_, list] of activePointers.entries()) {
        list.forEach(i => {
            if (!keyStates.has(i.keyId)) keyStates.set(i.keyId, { isRoot: !!i.isRoot });
            else if (i.isRoot) keyStates.set(i.keyId, { isRoot: true });
        });
    }

    const currentCache = shapeCache[CONFIG.keyShape] || shapeCache['original'];
    const isDrumMode = (currentInstId === 'drums');
    
    // Compute QWERTY offset once per frame (not per key)
    const qwertyOffset = (CONFIG.keyboardVisuals && window.QWERTY) 
        ? window.QWERTY.getLayoutOffset(canvas, metrics.w, CONFIG.rows) 
        : 0;
    
    // Pre-compute font sizes once
    const fontSize = Math.floor(metrics.w * 0.22);
    const g = CONFIG.gap;

    keys.forEach(k => {
        const s = keyStates.get(k.id);
        const isActive = !!s;

        let img;
        if (isActive) {
            img = s.isRoot ? currentCache.activeRoot : currentCache.activeOther;
        } else if (isDrumMode && (k.midi < 27 || k.midi > 88)) {
            img = currentCache.grey;
        } else {
            img = k.isWhite ? currentCache.white : currentCache.black;
        }

        if (img) {
            const offsetX = (img.width - k.w) / 2;
            const offsetY = (img.height - k.h) / 2;
            ctx.drawImage(img, k.x - offsetX, k.y - offsetY);
        }

        // Text labels (using pre-computed values)
        const dw = k.w - g, dh = k.h - g;
        const dx = k.x + g / 2, dy = k.y + g / 2;
        const cx = dx + dw / 2, cy = dy + dh / 2;
        
        ctx.fillStyle = isActive ? '#000' : ((isDrumMode && (k.midi < 27 || k.midi > 88)) ? '#666' : (k.isWhite ? '#555' : '#888'));
        ctx.font = "bold " + fontSize + "px sans-serif";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(k.note, cx, cy);

        // Octave numbers
        ctx.font = fontSize + "px sans-serif";
        ctx.fillStyle = isActive ? 'rgba(0,0,0,0.6)' : 'rgba(128,128,128,0.5)';
        ctx.fillText(Math.floor(k.midi / 12) - 1, cx, dy + dh - 10);
        
        // QWERTY labels (using pre-computed offset)
        if (CONFIG.keyboardVisuals && window.QWERTY) {
            window.QWERTY.renderKeyLabel(ctx, k, metrics.w, dx, dy, dw, qwertyOffset, CONFIG.colors.active);
        }
    });

    // Split octave lines
    if (CONFIG.splitOctave) {
        renderSplitLines(cssW);
    }
    // Render-on-demand: no continuous loop, render only when requestRender() is called
}

function renderSplitLines(cssW) {
    ctx.strokeStyle = CONFIG.colors.active;
    ctx.lineWidth = 4;

    let scaleFactor = 1.0;
    if (CONFIG.keyShape === 'hexagon') scaleFactor = 1.3;
    else if (CONFIG.keyShape === 'circle') scaleFactor = 1.12;
    else if (CONFIG.keyShape === 'diamond') scaleFactor = 2.0;

    const bleed = metrics.h * (scaleFactor - 1);
    const startOffY = bleed / 2;

    const split = CONFIG.splitParams;
    const amp = metrics.h * split.HexagonWaveHeight;
    const cAmp = metrics.h * split.CircleWaveHeight;

    const mWx = metrics.w;
    const shift = -(CONFIG.scrollPos * 6 * mWx) + (CONFIG.centerOffset || 0);
    const startX = (shift % mWx) - mWx;

    for (let r = 1; r < CONFIG.rows; r += 2) {
        if (r + 1 < CONFIG.rows) {
            const yBase = startOffY + (r + 1) * metrics.h;

            ctx.beginPath();
            if (CONFIG.keyShape === 'hexagon' || CONFIG.keyShape === 'diamond') {
                let useAmp = amp;
                let useY = yBase;

                if (CONFIG.keyShape === 'diamond') {
                    useAmp = metrics.h * DIAMOND_PARAMS.SPLIT_AMP;
                    useY += DIAMOND_PARAMS.SPLIT_OFFSET_Y;
                }

                ctx.moveTo(startX, useY + useAmp);
                for (let x = startX; x < cssW + mWx; x += mWx) {
                    ctx.lineTo(x + mWx * 0.5, useY - useAmp);
                    ctx.lineTo(x + mWx, useY + useAmp);
                }
            } else if (CONFIG.keyShape === 'circle') {
                const k = 0.18;

                ctx.moveTo(startX, yBase + cAmp);
                for (let x = startX; x < cssW + mWx; x += mWx) {
                    ctx.bezierCurveTo(
                        x + mWx * k, yBase + cAmp,
                        x + mWx * (0.5 - k), yBase - cAmp,
                        x + mWx * 0.5, yBase - cAmp
                    );
                    ctx.bezierCurveTo(
                        x + mWx * (0.5 + k), yBase - cAmp,
                        x + mWx * (1.0 - k), yBase + cAmp,
                        x + mWx, yBase + cAmp
                    );
                }
            } else {
                ctx.moveTo(0, yBase);
                ctx.lineTo(cssW, yBase);
            }
            ctx.stroke();
        }
    }
}

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

export function invertColor(hex) {
    if (hex.indexOf('#') === 0) hex = hex.slice(1);
    if (hex.length === 3) hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
    let r = (255 - parseInt(hex.slice(0, 2), 16)).toString(16);
    let g = (255 - parseInt(hex.slice(2, 4), 16)).toString(16);
    let b = (255 - parseInt(hex.slice(4, 6), 16)).toString(16);
    return '#' + pad(r) + pad(g) + pad(b);
}

function pad(s) {
    return (s.length === 1) ? '0' + s : s;
}

export function getKey(x, y) {
    return keys.find(k => x >= k.x && x <= k.x + k.w && y >= k.y && y <= k.y + k.h);
}
